import React from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  updateOnlineStatus,
  selectIsOnline,
  selectUpdatingStatus,
  selectStatusError,
  clearBusinessUserError,
} from "../redux/apis/Businessuser/onlinestatus";

// ============================================================
// 🟢 BUSINESS ONLINE STATUS
// ============================================================

const BusinessOnlineStatus = () => {
  const dispatch = useDispatch();

  // ==========================================================
  // 🔐 AUTH USER
  // ==========================================================

  const user = useSelector(
    (state) => state.auth.user
  );

  const isAuthenticated = useSelector(
    (state) => state.auth.isAuthenticated
  );

  // ==========================================================
  // 🔑 NORMALIZE USER ID
  // Supports:
  // user._id
  // user.id
  // ==========================================================

  const userId =
    user?._id || user?.id;

  // ==========================================================
  // 👤 BUSINESS ROLE
  // ==========================================================

  const isBusinessUser =
    user?.role === "business";

  // ==========================================================
  // 🟢 ONLINE STATUS REDUX
  // ==========================================================

  const isOnline = useSelector(
    selectIsOnline
  );

  const updatingStatus = useSelector(
    selectUpdatingStatus
  );

  const statusError = useSelector(
    selectStatusError
  );

  // ==========================================================
  // 🔄 TOGGLE ONLINE / OFFLINE
  // ==========================================================

  const handleToggleStatus = async () => {
    // ========================================================
    // AUTH CHECK
    // ========================================================

    if (
      !isAuthenticated ||
      !isBusinessUser ||
      !userId
    ) {
      console.error(
        "❌ Business user is not authenticated"
      );

      console.error(
        "isAuthenticated:",
        isAuthenticated
      );

      console.error(
        "user:",
        user
      );

      console.error(
        "userId:",
        userId
      );

      console.error(
        "role:",
        user?.role
      );

      return;
    }

    // ========================================================
    // PREVENT DOUBLE CLICK
    // ========================================================

    if (updatingStatus) {
      return;
    }

    // ========================================================
    // CLEAR OLD ERROR
    // ========================================================

    dispatch(
      clearBusinessUserError()
    );

    // ========================================================
    // CALCULATE NEW STATUS
    // ========================================================

    const newStatus = !isOnline;

    // ========================================================
    // DEBUG
    // ========================================================

    console.log(
      "======================================"
    );

    console.log(
      "🟢 ONLINE STATUS BUTTON CLICKED"
    );

    console.log(
      "User:",
      user
    );

    console.log(
      "User ID:",
      userId
    );

    console.log(
      "Current status:",
      isOnline
    );

    console.log(
      "New status:",
      newStatus
    );

    console.log(
      "Role:",
      user?.role
    );

    console.log(
      "======================================"
    );

    try {
      // ======================================================
      // API CALL
      // ======================================================

      const result =
        await dispatch(
          updateOnlineStatus({
            userId,
            isOnline: newStatus,
          })
        ).unwrap();

      // ======================================================
      // SUCCESS
      // ======================================================

      console.log(
        "======================================"
      );

      console.log(
        "✅ ONLINE STATUS UPDATED"
      );

      console.log(
        "Response:",
        result
      );

      console.log(
        "Backend status:",
        result?.user?.isOnline
      );

      console.log(
        "======================================"
      );

    } catch (error) {
      // ======================================================
      // ERROR
      // ======================================================

      console.error(
        "======================================"
      );

      console.error(
        "❌ ONLINE STATUS UPDATE FAILED"
      );

      console.error(
        "Error:",
        error
      );

      console.error(
        "======================================"
      );
    }
  };

  // ==========================================================
  // DON'T SHOW FOR LOGGED OUT / NON BUSINESS
  // ==========================================================

  if (
    !isAuthenticated ||
    !isBusinessUser ||
    !userId
  ) {
    return null;
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div
      className="
        flex
        flex-col
        items-center
        gap-2
      "
    >
      {/* =====================================================
          STATUS SWITCH
      ===================================================== */}

      <button
        type="button"
        onClick={handleToggleStatus}
        disabled={updatingStatus}
        aria-label={
          isOnline
            ? "Turn offline"
            : "Turn online"
        }
        title={
          isOnline
            ? "Click to go offline"
            : "Click to go online"
        }
        className={`
          relative
          w-24
          h-10
          rounded-full
          shadow-md
          transition-all
          duration-300
          focus:outline-none
          focus:ring-2
          focus:ring-offset-2

          ${
            isOnline
              ? `
                bg-green-500
                hover:bg-green-600
                focus:ring-green-400
              `
              : `
                bg-gray-400
                hover:bg-gray-500
                focus:ring-gray-400
              `
          }

          ${
            updatingStatus
              ? `
                opacity-60
                cursor-not-allowed
              `
              : `
                cursor-pointer
              `
          }
        `}
      >
        {/* OFF LABEL */}

        {!isOnline && (
          <span
            className="
              absolute
              left-3
              top-1/2
              -translate-y-1/2
              text-[11px]
              font-bold
              text-white
              pointer-events-none
            "
          >
            OFF
          </span>
        )}

        {/* ON LABEL */}

        {isOnline && (
          <span
            className="
              absolute
              right-3
              top-1/2
              -translate-y-1/2
              text-[11px]
              font-bold
              text-white
              pointer-events-none
            "
          >
            ON
          </span>
        )}

        {/* SWITCH CIRCLE */}

        <span
          className={`
            absolute
            top-1
            w-8
            h-8
            bg-white
            rounded-full
            shadow-lg
            transition-all
            duration-300
            flex
            items-center
            justify-center

            ${
              isOnline
                ? "left-[60px]"
                : "left-1"
            }
          `}
        >
          {updatingStatus ? (
            <span
              className="
                w-4
                h-4
                border-2
                border-gray-300
                border-t-purple-600
                rounded-full
                animate-spin
              "
            />
          ) : (
            <span className="text-xs">
              {isOnline
                ? "🟢"
                : "⚫"}
            </span>
          )}
        </span>
      </button>

      {/* =====================================================
          STATUS TEXT
      ===================================================== */}

      <div
        className="
          flex
          items-center
          gap-1.5
        "
      >
        <span
          className={`
            w-2.5
            h-2.5
            rounded-full

            ${
              isOnline
                ? "bg-green-500"
                : "bg-gray-400"
            }
          `}
        />

        <span
          className={`
            text-sm
            font-semibold

            ${
              isOnline
                ? "text-green-600"
                : "text-gray-500"
            }
          `}
        >
          {updatingStatus
            ? "Updating..."
            : isOnline
            ? "Online"
            : "Offline"}
        </span>
      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {statusError && (
        <p
          className="
            max-w-xs
            px-2
            text-center
            text-xs
            text-red-500
          "
        >
          {typeof statusError ===
          "string"
            ? statusError
            : statusError?.message ||
              "Unable to update status."}
        </p>
      )}
    </div>
  );
};

export default BusinessOnlineStatus;